<template>
  <v-menu v-model="showMenu"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px">
    <template v-slot:activator="{ on }">
      <edit-field ref="EDIT_FIELD"
                  :value="editValue"
                  :periods="periods"
                  :editFieldInfo="editFieldInfo"
                  :required="required"
                  :disabled="disabled"
                  :readOnly="true"
                  :onEvent="on"
                  :parentFocus="showMenu"
                  :hideLabel="hideLabel"
                  @updated=setUpdated
                  @error=setError
      >
      </edit-field>
    </template>
    <v-locale-provider locale="fr">
      <v-date-picker v-model="pickerComputed"
                     :min="pickerMin"
                     :max="pickerMax"
                     locale="fr-in"
                     no-title
                     multiple
                     first-day-of-week="1"
                     color="#e25c38"
                     :reactive="true">
      </v-date-picker>
    </v-locale-provider>
  </v-menu>
</template>

<script lang="ts">
import {FieldError, FieldInfo, FieldStatus} from '@/framework/base/components/FieldInfo';
import {DateFormat, Period} from '@/framework/base/helpers/DateFormat';
import EditField from '@/framework/base/components/EditField.vue';
import {defineComponent, type PropType} from "vue";

export default defineComponent({
  components: {EditField},
  props: {
    value: {
      type: String,
      required: true
    },
    periods: {
      type: Array as PropType<Period[]>,
      required: true
    },
    editFieldInfo: {
      type: Object as PropType<FieldInfo>,
      required: true
    },
    required: {
      type: Boolean
    },
    disabled: {
      type: Boolean
    },
    hideLabel: {
      type: Boolean
    },
    contextId: {
      type: Number
    }
  },
  created(): void {
    this.initValue = DateFormat.toEdit(this.value);
    this.editValue = this.initValue;
    this.lastValue = this.initValue;
    this.editFieldInfo.mask = null;
    this.editFieldInfo.focusValidator = null;
    this.editFieldInfo.liveValidator = null;
    this.editFieldInfo.valid = this.valid;
  },
  data() {
    const multipleDates: string[] = [];

    return {
      editValue: '',
      initValue: '',
      lastValue: '',
      editing: false,
      editError: false,
      showMenu: false,
      multipleDates,
      status: new FieldStatus(false, false, 0)
    };
  },
  computed: {
    pickerComputed: {
      get(): string[] {
        return this.multipleDates;
      },
      set(value: string[]) {
        const editValue: string[] = [];
        if (value.length > 0) {
          value.forEach((date: string) => editValue.push(DateFormat.toEdit(date)));
        }
        this.editValue = editValue.join(' - ');
        this.multipleDates = value;
        this.setUpdated(value);
      }
    },
    pickerMin(): string {
      let min = '2099-12-31';
      if (this.periods && this.periods.length > 0) {
        for (let i = 0; i < this.periods.length; i++) {
          if (!this.periods[i].min) {
            min = '1900-01-01';
          } else if (DateFormat.toNumber(this.periods[i].min) < DateFormat.toNumber(min)) {
            min = DateFormat.toPicker(this.periods[i].min);
          }
        }
      } else {
        min = '1900-01-01';
      }
      return min;
    },
    pickerMax(): string {
      let max = '1900-01-01';
      if (this.periods && this.periods.length > 0) {
        for (let i = 0; i < this.periods.length; i++) {
          if (!this.periods[i].max) {
            max = '2099-12-31';
          } else if (DateFormat.toNumber(this.periods[i].max) > DateFormat.toNumber(max)) {
            max = DateFormat.toPicker(this.periods[i].max);
          }
        }
      } else {
        max = '2099-12-31';
      }
      return max;
    }
  },

  methods: {
    setUpdated(value: string[]): void {
      const res: string[] = [];
      value.forEach((date: string) => res.push(DateFormat.toIso(date)));
      this.$emit('updated', res);
    },
    inPickerPeriod(val: string): string | null {
      return DateFormat.toPicker(this.checkAllowedDate(DateFormat.toEdit(val), this.periods));
    },
    valid(value: string, periods: Period[]): boolean {
      return !!this.checkAllowedDate(value, periods);
    },
    checkAllowedDate(val: string, periods: Period[]): string | null {
      if (!periods || periods.length == 0) {
        return val;
      }
      let res = null;
      if (DateFormat.toPicker(val)) {
        const valAsNumber = DateFormat.toNumber(val);

        for (let i = 0; i < periods.length; i++) {
          if (periods[i].min && periods[i].max) {
            if (valAsNumber >= DateFormat.toNumber(periods[i].min) && valAsNumber <= DateFormat.toNumber(periods[i].max)) {
              res = val;
            }
          } else if (periods[i].min && !periods[i].max) {
            if (valAsNumber >= DateFormat.toNumber(periods[i].min)) {
              res = val;
            }
          } else if (!periods[i].min && periods[i].max) {
            if (valAsNumber <= DateFormat.toNumber(periods[i].max)) {
              res = val;
            }
          } else {
            res = val;
          }
        }
      }

      return res;
    },
    setError(value: FieldError): void {
      if (value.error == false) {
        value.error = false;
      }
      this.signalError(value.error);
    },
    signalError(value: boolean): void {
      if (this.status.error != value) {
        this.status.error = value;
        this.$emit('error', new FieldError(0, '', value));
      }
    },
    touch(): void {
      // @ts-expect-error : generated method
      this.$refs.EDIT_FIELD.touch();
    },
    setFocus(): void {
      // @ts-expect-error : generated method
      this.$refs.EDIT_FIELD.focus();
    }
  }
})

</script>
